import './SMSSidePanel.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFlowBuilderContext } from 'pages/FlowBuilder/context/flow-builder.context';
import type { TSlidingSidePanelProps } from 'components/ExperienceCanvas/types';
import SlidingSidePanel from 'components/ExperienceCanvas/SlidingSidePanel/SlidingSidePanel';
import IconSMS from 'pages/FlowBuilder/assets/icons/IconSMS';
import SMSTemplatePreview from './SMSTemplatePreview/SMSTemplatePreview';
import TemplateSelector from '../shared/TemplateSelector/TemplateSelector';
import type {
  TSMSActionPayload,
  TSMSPreviewData,
  TSMSProviderTemplate,
  TSMSProviderTemplatePayload,
  TemplatePayloadData,
} from '../types';
import AttributesMappingTable, { AttributeErrorProps } from '../shared/AttributesMappingTable/AttributesMappingTable';
import useSettings from 'hooks/useSetting';
import { updateSMSTemplateData } from 'store/sms/sms.actions';
import useSMSTemplates from 'hooks/useSMSTemplates';
import useSMSTemplate from 'hooks/useSMSTemplate';
import { useThunkDispatch as useDispatch } from 'hooks/reduxHooks';
import { isEqual } from 'lodash';
import useConfirmNavigationModal from 'hooks/useConfirmNavigationModal';
import usePlugins, { TContextPlugin } from 'hooks/useContextPlugins';
import IconDiscardChanges from 'components/Shared/Icons/IconDiscardChanges';
import { capitalizeFirstCharacter } from 'helpers/common.helper';
import EmailSMSUnavailable from 'components/FlowBuilder/FlowSidePanels/shared/EmailSMSUnavailable/EmailSMSUnavailable';
import { deriveTemplatePayloadData, serializeTemplatePayloadData } from '../helpers';
import { getFormattedContextAttributeDisplay } from 'pages/FlowBuilder/helpers/flow-builder.helpers';

const MAIN_CLASS = 'sms-side-panel';
const CLASSES = {
  CONTAINER: `${MAIN_CLASS}__container`,
  TEMPLATE_AREA: `${MAIN_CLASS}__container__template-area`,
  PREVIEW_AREA: `${MAIN_CLASS}__container__preview-area`,
};

function SMSSidePanel() {
  const dispatch = useDispatch();
  const { previewTextLanguage: selectedLanguageCode } = useSettings();
  const { smsTemplates, smsTemplatesError } = useSMSTemplates();
  const providerTemplateOptions = useMemo(
    () =>
      smsTemplates.map((template) => ({
        key: template.id,
        name: template.name,
      })),
    [smsTemplates],
  );

  const { plugins }: { plugins: TContextPlugin[] } = usePlugins();

  const mappedAttributes = useMemo(() => {
    if (!plugins || plugins.length === 0) return [];
    const sorted = [...plugins]
      .sort((a, b) => a.id.localeCompare(b.id))
      .map((plugin) =>
        Object.entries(plugin.values).map(([attributeName, attribute]) => ({
          name: `${plugin.name} / ${attributeName}`,
          subText: [capitalizeFirstCharacter(attribute.valueType), `{{${attribute.id}}}`],
          id: attribute.uid,
          attribute,
        })),
      )
      .flat();

    return sorted;
  }, [plugins]);

  const [selectedProviderTemplate, setSelectedProviderTemplate] = useState<TSMSProviderTemplate | null>(null);
  const [currentTemplatePayloadData, setCurrentTemplatePayloadData] = useState<TemplatePayloadData | null>(null);

  const { smsTemplate, smsTemplateError, isSMSTemplateFetching, refetchSMSTemplate } = useSMSTemplate(
    selectedProviderTemplate?.key || '',
  );
  const {
    sidePanels: { sms: smsSidePanel },
  } = useFlowBuilderContext();
  const { isOpen, isViewOnly, sms: smsState } = smsSidePanel;

  const smsActionPayload = useMemo<TSMSActionPayload | null>(() => {
    if (!smsState?.payload) {
      return null;
    }
    const {
      payload: { template },
    } = smsState;
    return template?.[selectedLanguageCode] || null;
  }, [smsState, selectedLanguageCode]);

  const selectedProviderTemplatePayload = useMemo<TSMSProviderTemplatePayload | null>(() => {
    if (!selectedProviderTemplate || !smsTemplate?.isValid) {
      return null;
    }

    return smsTemplate;
  }, [selectedProviderTemplate, smsTemplate]);

  const defaultTemplatePayloadData = useMemo<TemplatePayloadData | null>(() => {
    if (
      !smsActionPayload ||
      !selectedProviderTemplatePayload ||
      smsActionPayload.providerTemplateId !== selectedProviderTemplatePayload.id
    )
      return null;

    const templatePayloadData = deriveTemplatePayloadData(
      selectedProviderTemplatePayload.placeholderKeys || [],
      smsActionPayload.providerTemplateData || {},
      mappedAttributes,
    );

    return templatePayloadData;
  }, [smsActionPayload, selectedProviderTemplatePayload, mappedAttributes]);

  const payloadDataErrors = useMemo(() => {
    if (!currentTemplatePayloadData && !defaultTemplatePayloadData) {
      return undefined;
    }
    const errors: Record<string, AttributeErrorProps> = {};

    Object.entries({ ...(defaultTemplatePayloadData ?? {}), ...currentTemplatePayloadData }).forEach(([key, value]) => {
      const attributeId = value.attribute.match(/{{(.*?)}}/)?.[1];
      const mappedValue = attributeId
        ? mappedAttributes.find((plugin) => plugin.attribute.uid === attributeId)
        : undefined;
      const attributeParameters = value.attributeParameters;

      if (attributeId && !mappedValue) {
        errors[key] = {
          errorMessage: `This placeholder uses a plugin that does not exist or is no longer available`,
          errorType: 'error',
        };
      } else if (attributeId && mappedValue?.attribute.parameters?.length && !attributeParameters?.length) {
        errors[key] = {
          errorMessage: `This context plugin requires you to fill in the parameter values. Click the edit button to do so.`,
          errorType: 'error',
        };
      } else if (!selectedProviderTemplatePayload?.placeholderKeys?.includes(key)) {
        errors[key] = {
          errorMessage: `This placeholder is not being used by the Sendgrid template`,
          errorType: 'warning',
        };
      }
    });

    return errors;
  }, [currentTemplatePayloadData, defaultTemplatePayloadData, selectedProviderTemplatePayload, mappedAttributes]);

  const saveDisabled = payloadDataErrors
    ? !!Object.values(payloadDataErrors).find((error) => error.errorType === 'error')
    : false;

  const inStorePayloadData = useMemo<TemplatePayloadData | null>(() => {
    if (!smsActionPayload) return null;
    return deriveTemplatePayloadData(
      (smsActionPayload?.journeySMSTemplateId === selectedProviderTemplatePayload?.id &&
        selectedProviderTemplatePayload?.placeholderKeys) ||
        [],
      smsActionPayload?.journeySMSTemplateData ?? {},
      mappedAttributes,
    );
  }, [smsActionPayload, selectedProviderTemplatePayload, mappedAttributes]);

  // Determine if there are unsaved changes using diffObject util
  const hasUnsavedChanges = useMemo(() => {
    if (smsActionPayload?.journeySMSTemplateId !== selectedProviderTemplatePayload?.id) return true;
    return !isEqual(inStorePayloadData, currentTemplatePayloadData);
  }, [
    inStorePayloadData,
    currentTemplatePayloadData,
    smsActionPayload?.journeySMSTemplateId,
    selectedProviderTemplatePayload?.id,
  ]);

  const performClose = useCallback(() => {
    smsSidePanel.setSMSContext((prev) => ({ ...prev, isOpen: false }));
    setCurrentTemplatePayloadData(null);
    setSelectedProviderTemplate(null);
  }, [smsSidePanel]);

  const [ConfirmNavigationModal, showConfirmNavigationModal] = useConfirmNavigationModal({
    icon: <IconDiscardChanges />,
    title: 'Cancel changes?',
    description: 'Navigating back will undo current changes made to mapping.',
    primaryAction: { value: 'Remain on page' },
    secondaryAction: { value: 'Discard changes' },
  });

  const onClose = useCallback(async () => {
    // If there are no unsaved changes, close immediately
    if (!hasUnsavedChanges) {
      performClose();
      return;
    }

    // If there ARE unsaved changes, only close if user confirms navigation
    const shouldNavigateAway = await showConfirmNavigationModal();
    if (shouldNavigateAway) {
      performClose();
    }
  }, [hasUnsavedChanges, performClose, showConfirmNavigationModal]);

  const onSave = useCallback(() => {
    // Saving should NOT trigger confirmation modal even if there are unsaved changes.
    if (currentTemplatePayloadData) {
      dispatch(
        updateSMSTemplateData(
          serializeTemplatePayloadData(
            selectedProviderTemplatePayload?.placeholderKeys || [],
            currentTemplatePayloadData || {},
          ),
          selectedProviderTemplate?.key ?? smsActionPayload?.providerTemplateId ?? '',
          smsState?.refId || '',
          selectedLanguageCode,
        ),
      );
    }
    performClose();
  }, [
    dispatch,
    smsActionPayload?.providerTemplateId,
    smsState?.refId,
    performClose,
    selectedLanguageCode,
    selectedProviderTemplate?.key,
    currentTemplatePayloadData,
    selectedProviderTemplatePayload?.placeholderKeys,
  ]);

  const handleSetSelectedProviderTemplate = useCallback(
    (template: TSMSProviderTemplate) => {
      if (template.key === selectedProviderTemplate?.key) {
        return;
      }
      setCurrentTemplatePayloadData(null);
      setSelectedProviderTemplate(template);
    },
    [selectedProviderTemplate?.key],
  );

  const handleConfirmSyncTemplate = useCallback(async () => {
    const result = await refetchSMSTemplate();

    if (result.error) {
      dispatch({
        type: 'SHOW_SNACKBAR',
        payload: { content: 'Unable to sync Twilio template, please try again.', type: 'error' },
      });
    } else {
      dispatch({
        type: 'SHOW_SNACKBAR',
        payload: { content: 'Twilio template has been synced.', type: 'success' },
      });
    }
  }, [refetchSMSTemplate, dispatch]);

  const previewData = useMemo<TSMSPreviewData | null>(() => {
    if (!selectedProviderTemplatePayload || !currentTemplatePayloadData) {
      return null;
    }
    const data = Object.entries(currentTemplatePayloadData).reduce((acc, [key, value]) => {
      const mappedValue = mappedAttributes.find(
        (plugin) => plugin.attribute.uid === value.attribute.match(/{{(.*?)}}/)?.[1],
      );
      if (mappedValue) {
        acc[key] = getFormattedContextAttributeDisplay(mappedValue.name, value.attributeParameters);
        return acc;
      }
      acc[key] = value.attribute;
      return acc;
    }, {} as Record<string, string>);

    return {
      title: selectedProviderTemplatePayload.name,
      body: selectedProviderTemplatePayload.body,
      data,
    };
  }, [selectedProviderTemplatePayload, currentTemplatePayloadData, mappedAttributes]);

  const slidingSidePanelProps = useMemo<TSlidingSidePanelProps>(() => {
    const sidePanelProps: TSlidingSidePanelProps = {
      show: isOpen,
      headerInfo: {
        mainTitle: '',
        goBackIcon: <IconSMS />,
        goBackTitle: 'Setup your SMS',
        goBackSubTitle: 'Please provide the details for the SMS you want to send to your target audience',
        goBackActionHandler: () => {
          onClose();
        },
      },
      footerInfo: {
        secondaryActionHandler: () => {
          onClose();
        },
        secondaryActionText: 'Back',
        // Disable footer buttons while syncing template or while template is (re)loading
        secondaryActionDisabled: isSMSTemplateFetching,
      },
      size: 'full',
      isViewOnly,
      viewOnlyHeader: {
        text: 'This is an active experience',
        subText: 'To update the template and its associated data mapping, please deactivate the experience first.',
      },
    };

    if (!isViewOnly) {
      sidePanelProps.footerInfo = {
        ...sidePanelProps.footerInfo,
        primaryActionHandler: () => {
          onSave();
        },
        primaryActionText: 'Apply',
        primaryActionDisabled: isSMSTemplateFetching || saveDisabled,
        primaryActionLoading: isSMSTemplateFetching,
      };
    }

    return sidePanelProps;
  }, [onClose, onSave, isOpen, isViewOnly, isSMSTemplateFetching, saveDisabled]);

  useEffect(() => {
    if (selectedProviderTemplatePayload) {
      setCurrentTemplatePayloadData(
        deriveTemplatePayloadData(
          selectedProviderTemplatePayload?.placeholderKeys ?? [],
          smsActionPayload?.journeySMSTemplateId === selectedProviderTemplatePayload?.id
            ? smsActionPayload?.journeySMSTemplateData ?? {}
            : {},
          mappedAttributes,
        ),
      );
    }
  }, [selectedProviderTemplatePayload, smsActionPayload, mappedAttributes]);

  useEffect(() => {
    if (providerTemplateOptions.length > 0 && isOpen)
      setSelectedProviderTemplate(
        providerTemplateOptions.find((t) => t.key === smsActionPayload?.journeySMSTemplateId) || null,
      );
  }, [smsActionPayload?.journeySMSTemplateId, providerTemplateOptions, isOpen]);

  return (
    <div className={MAIN_CLASS} key={`sms-side-panel-${smsState?.refId || ''}-${selectedLanguageCode}`}>
      <SlidingSidePanel {...slidingSidePanelProps}>
        {smsTemplatesError || !smsTemplates || smsTemplates.length === 0 ? (
          <EmailSMSUnavailable type="sms" />
        ) : (
          <div
            className={`${CLASSES.CONTAINER} ${isViewOnly ? `${CLASSES.CONTAINER}--view-only` : ''}`}
            {...{ inert: isViewOnly ? '' : undefined }}
          >
            <div className={CLASSES.TEMPLATE_AREA}>
              <TemplateSelector
                type="sms"
                onTemplateSelect={handleSetSelectedProviderTemplate}
                templates={providerTemplateOptions}
                selectedTemplate={selectedProviderTemplate}
                isTemplateError={!!smsTemplateError}
              />
              <AttributesMappingTable
                type="sms"
                defaultTemplatePayloadData={defaultTemplatePayloadData}
                providerTemplatePayloadData={currentTemplatePayloadData}
                payloadDataErrors={payloadDataErrors}
                isLoading={isSMSTemplateFetching}
                onChange={setCurrentTemplatePayloadData}
                onConfirmSyncTemplate={handleConfirmSyncTemplate}
                mappedAttributes={mappedAttributes}
                isTemplateError={!!smsTemplateError}
              />
            </div>
            <div className={CLASSES.PREVIEW_AREA}>
              <SMSTemplatePreview
                previewData={previewData}
                isLoading={isSMSTemplateFetching}
                isTemplateError={!!smsTemplateError}
              />
            </div>
          </div>
        )}
        {ConfirmNavigationModal()}
      </SlidingSidePanel>
    </div>
  );
}

export default SMSSidePanel;
